import { Event, Prisma, PrismaClient } from "@prisma/client";
import { TRPCError } from "@trpc/server";
import { IPrisma } from "~/server/db";
import { GetPermissions } from "./permissionHelpers";
import { capitalize } from "~/utils/string";
import { CustomizableLayout } from "~/types/CustomizableLayout";
import { DEFAULT_FP_CUSTOMIZATION } from "~/app/beta/(white-label)/freepress/utils/constants";

export const CommunityHelpers = {
    getBySlug: async (prisma: IPrisma, slug?: string) => {
        if (!slug) {
            throw new TRPCError({
                code: "BAD_REQUEST",
                message: "No Community slug provided",
            });
        }

        return await prisma.community.findFirstOrThrow({
            where: {
                slug: slug,
            },
            include: {
                links: true,
                topHosts: true,
                managers: true,
                leaders: true,
            },
        });
    },

    getAllHomePageCommunities: async (prisma: IPrisma) => {
        return await prisma.community.findMany({
            where: {
                displayOnHomePage: true,
                status: "PUBLISHED",
            },
            orderBy: {
                featureOrder: "asc",
            },
            select: {
                id: true,
                name: true,
                slug: true,
            },
        });
    },

    getMembersCount: async (
        prisma: IPrisma,
        communityIds: string[],
    ): Promise<Map<string, number>> => {
        if (communityIds.length === 0) return new Map();
        const counts: { communityId: string; count: number }[] =
            await prisma.$queryRaw`
        SELECT e."communityId", COUNT(DISTINCT a."userId")
            FROM "Event" e
            JOIN "Attendee" a ON e.id = a."eventId"
                WHERE e."communityId" IN (${Prisma.join(communityIds)})
                AND e."eventJourneyStatus" = 'PUBLISHED'
            GROUP BY e."communityId"`;
        return counts.reduce((map, result) => {
            map.set(result.communityId, Number(result.count));
            return map;
        }, new Map());
    },

    getTotalEvents: async (prisma: IPrisma, communityId: string) => {
        // count of events with at least 1 host
        const counts: { count: number }[] = await prisma.$queryRaw`
                SELECT COUNT(DISTINCT e.id)
                    FROM "Event" e
                    JOIN "Host" h ON e.id = h."eventId"
                        WHERE e."communityId" = ${communityId}
                        AND e."eventJourneyStatus" = 'PUBLISHED'
                    GROUP BY e.id
                    HAVING COUNT(h.id) >= 1
            `;
        return counts.length;
    },

    getTotalFutureAttendees: async (prisma: IPrisma, communityId: string) => {
        // count of events with at least 3 attendees
        const data: { sum: number } = await prisma.$queryRaw`
                    SELECT SUM(subquery.attendee_count::int)
                    FROM (
                        SELECT COUNT(a.id) AS attendee_count
                            FROM "Event" e
                            JOIN "Attendee" a ON e.id = a."eventId"
                                WHERE e."communityId" =  ${communityId}
                                AND e."eventJourneyStatus" = 'PUBLISHED'
                                AND e."startTime" >= NOW()
                            GROUP BY e.id
                            -- HAVING COUNT(a.id) >= 3
                    ) subquery;
            `;
        return data.sum;
    },

    isMember: (
        permissions: GetPermissions,
        event: Pick<
            Event,
            "communityId" | "isMembersOnlyTagGated" | "resourceTags"
        >,
    ) => {
        if (permissions.isAdmin) return true;
        if (!event.communityId) return false;
        if (!permissions.memberships?.length) return false;
        if (!event.isMembersOnlyTagGated) return true;

        // check if there's a match between membership tags and events
        for (const membership of permissions.memberships) {
            if (
                event.resourceTags.filter((tag) =>
                    membership.tags.includes(tag),
                ).length === membership.tags.length
            )
                return true;
        }

        return false;
    },

    // TODO: We will eventually need to do this, but only when db gets
    // fucked up again. Going to ignore for now :-)
    clearFeatureOrderFromNonHomePageCommunities: async (prisma: IPrisma) => {
        throw new Error("Not implemented");
    },
};

export const getCommunityTopHosts = async (
    prisma: PrismaClient,
    communitySlug: string,
    take = 15,
) => {
    const users = await prisma.user.findMany({
        where: {
            hostedEvents: {
                some: {
                    event: {
                        community: {
                            slug: communitySlug,
                        },
                    },
                },
            },
        },
        select: {
            id: true,
            name: true,
            image: true,
            socialLinks: true,
            slug: true,
            _count: {
                select: {
                    hostedEvents: {
                        where: {
                            event: {
                                community: {
                                    slug: communitySlug,
                                },
                                eventJourneyStatus: "PUBLISHED",
                            },
                        },
                    },
                },
            },
        },
        orderBy: {
            hostedEvents: {
                _count: "desc",
            },
        },
    });

    // get all feedbacks for each host
    const feedbacks = await prisma.hostFeedback.findMany({
        where: {
            host: {
                user: {
                    id: {
                        in: users.map((user) => user.id),
                    },
                },
            },
        },
        select: {
            rating: true,
            host: {
                select: {
                    user: {
                        select: {
                            id: true,
                            name: true,
                            image: true,
                        },
                    },
                },
            },
        },
    });

    const feedbacksByHost: Record<string, number[]> = feedbacks.reduce(
        (acc, feedback) => {
            if (!acc[feedback.host.user.id]) {
                acc[feedback.host.user.id] = [];
            }

            acc[feedback.host.user.id]!.push(feedback.rating);

            return acc;
        },
        {} as Record<string, number[]>,
    );

    const usersWithFeedback = users.map((user) => {
        const feedbacks = feedbacksByHost[user.id] || [];
        const averageRating =
            feedbacks.length > 0
                ? feedbacks.reduce((sum, rating) => sum + rating, 0) /
                  feedbacks.length
                : 0;

        const rankPoints = user._count.hostedEvents * averageRating;

        return {
            ...user,
            averageRating,
            rankPoints,
        };
    });

    return usersWithFeedback
        .sort((a, b) => b.rankPoints - a.rankPoints)
        .slice(0, take);
};

export const FREEPRESS_THEME = {
    colors: {
        background: "bg-[#f2ede9]",
        container: "bg-white",
    },
    components: {
        communityHeader: false,
        communityFooter: false,
        simpleCommunityFooter: true,
    },
    font: "font-serif",
    footerLogoUrl: `https://freepress.rvr.to/white-label/freepress/thefp-logo-on-black.png`,
};

export class WhitelabeledCommunity {
    slug: string;
    domain: string;
    theme: typeof FREEPRESS_THEME;
    subscriptionUrl: string;
    hostingLink?: string;
    layout?: CustomizableLayout;
    name?: string;

    constructor(
        slug: string,
        domain: string,
        theme: typeof FREEPRESS_THEME,
        subscriptionUrl: string,
        hostingLink?: string,
        layout?: CustomizableLayout,
        name?: string,
    ) {
        this.slug = slug;
        this.domain = domain;
        this.theme = theme;
        this.subscriptionUrl = subscriptionUrl;
        this.hostingLink = hostingLink;
        this.layout = layout;
        this.name = name;
    }

    getUrl() {
        return `https://${this.domain}${this.domain.includes("-dev") ? ":3000" : ""}`;
    }

    getEmail() {
        return {
            email: `hello@${this.domain}`,
            name: capitalize(this.slug),
        };
    }
}

export const WHITELABELED_COMMUNITIES = [
    {
        slug: "freepress",
        name: "Free Press",
        domain: "freepress.rvr.to",
        hostingLink:
            "https://docs.google.com/document/d/1EU_FqhDG6cN0h1BwKFQ24gyoTn_5PSKbPY2RrWFAY7w/edit?tab=t.0#heading=h.66hp3qyrfqyr",
        theme: FREEPRESS_THEME,
        subscriptionUrl: "https://www.thefp.com/subscribe",
        layout: DEFAULT_FP_CUSTOMIZATION,
    },
    {
        slug: "freepress",
        domain: "events.thefp.com",
        name: "Free Press",
        hostingLink:
            "https://docs.google.com/document/d/1EU_FqhDG6cN0h1BwKFQ24gyoTn_5PSKbPY2RrWFAY7w/edit?tab=t.0#heading=h.66hp3qyrfqyr",
        theme: FREEPRESS_THEME,
        subscriptionUrl: "https://www.thefp.com/subscribe",
        layout: DEFAULT_FP_CUSTOMIZATION,
    },
    {
        slug: "freepress-dev",
        domain: "freepress-dev.rvr.to",
        name: "Free Press",
        hostingLink:
            "https://docs.google.com/document/d/1EU_FqhDG6cN0h1BwKFQ24gyoTn_5PSKbPY2RrWFAY7w/edit?tab=t.0#heading=h.66hp3qyrfqyr",
        theme: FREEPRESS_THEME,
        subscriptionUrl: "https://www.thefp.com/subscribe",
        layout: DEFAULT_FP_CUSTOMIZATION,
    },
].map(
    (c) =>
        new WhitelabeledCommunity(
            c.slug,
            c.domain,
            c.theme,
            c.subscriptionUrl,
            c.hostingLink,
            c.layout,
            c.name,
        ),
);

export const getWhiteLabelSlug = (
    pathname?: string | null,
    hostname?: string | null,
) => {
    if (!pathname && !hostname) return undefined;
    if (pathname) {
        return WHITELABELED_COMMUNITIES.find((c) => pathname.includes(c.slug))
            ?.slug;
    }
    if (hostname) {
        return WHITELABELED_COMMUNITIES.find((c) => hostname === c.slug)?.slug;
    }
    return undefined;
};

export const getWhitelabeledCommunity = (slug: string | undefined) => {
    if (!slug) return undefined;
    return WHITELABELED_COMMUNITIES.find((c) => c.slug === slug);
};

export const getWhitelabeledCommunityBy = (
    field: keyof WhitelabeledCommunity,
    value: string | undefined,
) => {
    if (!value) return undefined;
    return WHITELABELED_COMMUNITIES.find((c) => c[field] === value);
};

export const getWhitelabeledCommunityUrl = (slug: string | undefined) => {
    const community = getWhitelabeledCommunity(slug);
    if (!community) return undefined;

    return community.getUrl();
};

export const getWhitelabeledCommunityEmail = (slug: string | undefined) => {
    const community = getWhitelabeledCommunity(slug);
    if (!community) return undefined;
    return community.getEmail();
};
